import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Briefcase, FileText, Tag, DollarSign, BarChart2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreatorPayoutDetail() {
  const { payoutId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios
      .get(`${API}/creator/payouts/${payoutId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Payout not found or access denied'))
      .finally(() => setLoading(false));
  }, [payoutId]);

  if (loading)
    return <div style={{ padding: 60, textAlign: 'center', color: theme.colors.text.muted }}>Loading...</div>;
  if (!data || !data.payout)
    return <div style={{ padding: 60, textAlign: 'center', color: theme.colors.text.muted }}>Not found</div>;

  const p = data.payout;
  const c = data.cycle;
  const portfolio = data.portfolio;
  const cb = p.calculation_basis || {};
  const statusColors = { PENDING: '#D97706', APPROVED: '#2563EB', SCHEDULED: '#0EA5E9', PAID: '#059669' };
  const sc = statusColors[p.status] || theme.colors.text.muted;

  return (
    <div data-testid="creator-payout-detail-page" style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px' }}>
      <button
        data-testid="creator-payout-detail-back"
        onClick={() => navigate('/creator/payouts')}
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.primary, fontSize: 14, fontWeight: 600, marginBottom: 24 }}
      >
        <ArrowLeft size={16} /> Back to Payouts
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <h1 data-testid="creator-payout-amount" style={{ fontSize: 28, fontWeight: 700, color: theme.colors.text.primary, margin: 0 }}>
          ${Number(p.amount || 0).toFixed(2)} {p.currency}
        </h1>
        <span
          data-testid="creator-payout-status"
          style={{ padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: `${sc}1A`, color: sc }}
        >
          {p.status}
        </span>
      </div>
      <p style={{ fontSize: 13, color: theme.colors.text.muted, marginBottom: 24 }}>Payout ID: {p.id}</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Briefcase size={14} color={theme.colors.primary} />
              <span style={{ fontSize: 12, color: theme.colors.text.muted }}>Portfolio</span>
            </div>
            <p
              data-testid="creator-payout-portfolio"
              style={{ fontSize: 14, fontWeight: 600, color: theme.colors.primary, cursor: 'pointer' }}
              onClick={() => p.portfolio_id && navigate(`/creator/portfolios/${p.portfolio_id}`)}
            >
              {p.portfolio_name}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Calendar size={14} color="#0EA5E9" />
              <span style={{ fontSize: 12, color: theme.colors.text.muted }}>Cycle</span>
            </div>
            <p data-testid="creator-payout-cycle" style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text.primary }}>{c?.cycle_name || '-'}</p>
            <p style={{ fontSize: 11, color: theme.colors.text.muted, marginTop: 2 }}>{c?.period_start?.slice(0, 10)} → {c?.period_end?.slice(0, 10)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Tag size={14} color="#9333EA" />
              <span style={{ fontSize: 12, color: theme.colors.text.muted }}>Holder</span>
            </div>
            <p data-testid="creator-payout-buyer" style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text.primary }}>{p.buyer_email_masked || '***'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <DollarSign size={14} color="#059669" />
              <span style={{ fontSize: 12, color: theme.colors.text.muted }}>Method</span>
            </div>
            <p style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text.primary }}>{p.payout_method || p.method || 'USD'}</p>
          </CardContent>
        </Card>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <CardContent className="pt-5 pb-5">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <BarChart2 size={16} color={theme.colors.primary} />
            <h3 style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text.primary }}>Calculation Basis</h3>
          </div>
          <div data-testid="creator-payout-basis" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
            {[
              ['Sales Count', cb.sales_count ?? 0],
              ['Holder Units', cb.buyer_units ?? 0],
              ['Gross Revenue', `$${Number(cb.gross_revenue || 0).toFixed(2)}`],
              ['Kickback %', `${cb.kickback_percent ?? 0}%`],
              ['Final Payout', `$${Number(p.amount || 0).toFixed(2)}`],
            ].map(([k, v]) => (
              <div key={k}>
                <p style={{ fontSize: 11, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{k}</p>
                <p style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text.primary }}>{v}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {portfolio && (
        <Card>
          <CardContent className="pt-5 pb-5">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <FileText size={16} color={theme.colors.text.muted} />
              <h3 style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text.primary }}>Portfolio</h3>
            </div>
            <p style={{ fontSize: 13, color: theme.colors.text.secondary }}>
              {portfolio.product_name || portfolio.name} · Unit Price ${portfolio.unit_price ?? 0} · Kickback {portfolio.kickback_percent ?? 0}%
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
